'use client';

import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { useState } from 'react';

type Mode = 'year' | 'last' | 'custom';

function lastCompleteMonth(): string {
	const d = new Date();
	d.setDate(1);
	d.setMonth(d.getMonth() - 1);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function addMonths(ym: string, n: number): string {
	const [y, m] = ym.split('-').map(Number);
	const d = new Date(Date.UTC(y!, m! - 1 + n, 1));
	return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export interface GseXlsxExportCardProps {
	busy: boolean;
	/** Richiede la generazione del pacchetto per i mesi [from, to] (YYYY-MM) */
	onExport: (from: string, to: string) => void;
}

/**
 * Esporta uno zip di workbook xlsx simulati nel formato GSE «Dett_Misura»
 * (un file PRELEVATA/IMMESSA per POD per mese): ultimo anno, ultimo mese
 * completo o un mese specifico.
 */
export function GseXlsxExportCard({ busy, onExport }: GseXlsxExportCardProps) {
	const lastMonth = lastCompleteMonth();
	const [mode, setMode] = useState<Mode>('last');
	const [customMonth, setCustomMonth] = useState(lastMonth);

	function handleExport() {
		const to = mode === 'custom' ? customMonth : lastMonth;
		if (!/^\d{4}-\d{2}$/.test(to)) return;
		const from = mode === 'year' ? addMonths(lastMonth, -11) : to;
		onExport(from, to);
	}

	return (
		<div className="card">
			<div className="card-head">
				<span className="card-title">
					<FileSpreadsheet className="mr-1 inline h-3.5 w-3.5 text-accent-purple" />
					Simulazione report GSE (xlsx)
				</span>
				<span className="card-badge badge-purple">DATI DI TEST</span>
			</div>

			<p className="mb-3 text-[12px] text-text-muted">
				Genera i workbook «Dett_Misura» che il GSE pubblica per ogni punto di misura (fogli
				Riepilogo + Curva oraria): un file PRELEVATA per ogni POD di prelievo e un file IMMESSA per
				ogni POD di immissione, raccolti in uno zip per mese. Stesse curve simulate del CSV
				quartorario - nessuna lettura viene scritta.
			</p>

			<div className="flex flex-wrap items-center gap-2">
				<select
					value={mode}
					onChange={(e) => setMode(e.target.value as Mode)}
					disabled={busy}
					aria-label="Periodo di esportazione"
					className="rounded border border-border bg-background px-2 py-1 text-[13px] text-text-primary"
				>
					<option value="last">Ultimo mese completo</option>
					<option value="year">Ultimo anno (12 mesi completi)</option>
					<option value="custom">Mese specifico…</option>
				</select>

				{mode === 'custom' && (
					<input
						type="month"
						value={customMonth}
						max={lastMonth}
						onChange={(e) => setCustomMonth(e.target.value)}
						disabled={busy}
						aria-label="Mese specifico"
						className="rounded border border-border bg-background px-2 py-1 text-[13px] text-text-primary"
					/>
				)}

				<button
					type="button"
					onClick={handleExport}
					disabled={busy}
					className="inline-flex items-center gap-1.5 rounded border border-accent-purple/50 bg-accent-purple/10 px-3 py-1 text-[13px] text-accent-purple transition-colors hover:bg-accent-purple/20 disabled:opacity-50"
				>
					{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
					{busy ? 'Generazione in corso…' : 'Genera e scarica ZIP xlsx'}
				</button>
			</div>

			<p className="mt-2 text-[11px] italic text-text-muted">
				Lo zip contiene una cartella per mese con un xlsx per punto di misura, nello stesso formato
				dei file scaricabili dal portale GSE.
			</p>
		</div>
	);
}
